import { Button } from '@mui/material';
import React from 'react';

/**
 * Styled button component with RSM Collective corner design
 * Square corners on top-left and bottom-right, rounded on top-right and bottom-left
 *
 * @param {Object} props - Button props
 * @param {string} [props.variant] - Button variant (contained, outlined, text, link)
 * @param {string} [props.size] - Button size (small, medium, large)
 * @param {React.ReactNode} props.children - Button content
 * @param {Function} [props.onClick] - Click handler
 * @param {Object} [props.sx] - Additional MUI sx props
 * @param {boolean} [props.fullWidth] - Whether button should be full width
 * @param {string} [props.color] - Button color
 * @param {string} [props.type] - Button type (button, submit, reset)
 * @param {React.ReactNode} [props.startIcon] - Icon to display before button text
 * @param {React.ReactNode} [props.endIcon] - Icon to display after button text
 * @returns {JSX.Element} Styled button component
 */
function StyledButton({
  variant = 'contained',
  size = 'medium',
  children,
  onClick,
  sx = {},
  fullWidth = false,
  color = 'var(--color-primary)',
  type = 'button',
  startIcon,
  endIcon,
  ...props
}) {
  // For link variant (like Back to Blog), use simpler styling but keep corner design
  if (variant === 'link') {
    return (
      <Button
        variant="text"
        size={size}
        onClick={onClick}
        startIcon={startIcon}
        endIcon={endIcon}
        sx={{
          color: color,
          fontWeight: 600,
          textTransform: 'none',
          px: startIcon || endIcon ? 2 : 1.5,
          py: 1.25,
          borderTopLeftRadius: 0,
          borderTopRightRadius:
            size === 'large' ? '24px' : size === 'small' ? '12px' : '20px',
          borderBottomLeftRadius:
            size === 'large' ? '24px' : size === 'small' ? '12px' : '20px',
          borderBottomRightRadius: 0,
          border: `2px solid transparent`,
          minWidth: 'auto',
          '&:hover': {
            backgroundColor: `${color}11`,
            borderColor: color,
            textDecoration: 'none',
          },
          ...sx,
        }}
        {...props}
      >
        {children}
      </Button>
    );
  }

  const baseStyles = {
    textTransform: 'none',
    fontWeight: 600,
    borderTopLeftRadius: 0,
    borderTopRightRadius:
      size === 'large' ? '24px' : size === 'small' ? '12px' : '20px',
    borderBottomLeftRadius:
      size === 'large' ? '24px' : size === 'small' ? '12px' : '20px',
    borderBottomRightRadius: 0,
    transition: 'all 0.2s ease',
  };

  const variantStyles = {
    contained: {
      backgroundColor: color,
      color: 'white',
      '&:hover': {
        backgroundColor:
          color === 'var(--color-primary)'
            ? 'var(--color-primary-dark)'
            : `${color}dd`,
        transform: 'translateY(-1px)',
      },
    },
    outlined: {
      backgroundColor: 'transparent',
      color: color,
      border: `2px solid ${color}`,
      '&:hover': {
        backgroundColor: `${color}11`,
        borderColor: color,
        transform: 'translateY(-1px)',
      },
    },
    text: {
      color: color,
      '&:hover': {
        backgroundColor: `${color}11`,
      },
    },
  };

  const sizeStyles = {
    small: {
      py: 1,
      px: 2,
      fontSize: '0.875rem',
    },
    medium: {
      py: 1.5,
      px: 3,
      fontSize: '1rem',
    },
    large: {
      py: 2,
      px: 4,
      fontSize: '1.125rem',
    },
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={onClick}
      fullWidth={fullWidth}
      type={type}
      startIcon={startIcon}
      endIcon={endIcon}
      sx={{
        ...baseStyles,
        ...variantStyles[variant],
        ...sizeStyles[size],
        ...sx,
      }}
      {...props}
    >
      {children}
    </Button>
  );
}

export default StyledButton;
